// frontend/app/components/admin/SeededLessonsPanel.js
//
// "Remove seeded lessons" / "Restore seeded lessons" for the Lesson Seeding tab.
//
// Removal is a soft delete on the server (nothing is ever hard-deleted, so
// student evidence survives), which is why Restore exists and why the confirm
// step is an in-app preview rather than a browser confirm(): it shows how many
// lessons go, and which journeys will show them as unavailable.

import React, { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import axios from "axios";
import { useTranslation } from "../../i18n/useTranslation";
import { ADMIN_LESSON_COLORS as colors } from "./adminLessonColors";

const SEEDED_URL = "/admin/lesson-plans/seeded";

const readError = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail) return JSON.stringify(detail);
  return fallback;
};

const SeededLessonsPanel = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(SEEDED_URL);
      setData(res?.data || null);
      setLoadError(null);
    } catch (err) {
      setData(null);
      setLoadError(t("Could not load seeded lessons."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const lessons = Array.isArray(data?.lessons) ? data.lessons : [];
  const live = lessons.filter((l) => !l.is_deleted);
  const hidden = lessons.filter((l) => l.is_deleted);
  const journeys = Array.isArray(data?.affected_journeys) ? data.affected_journeys : [];
  const forks = Number(data?.forks) || 0;

  const run = async (action, doneKey) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      const res = await axios.post(`${SEEDED_URL}/${action}`);
      setMessage(t(doneKey, { count: res?.data?.count ?? 0 }));
      setPreviewOpen(false);
      await load();
    } catch (err) {
      setActionError(readError(err, t("Could not update seeded lessons.")));
    } finally {
      setBusy(false);
    }
  };

  const canRemove = !busy && live.length > 0;
  const canRestore = !busy && hidden.length > 0;

  return (
    <View style={styles.card}>
      <Text style={styles.help}>
        {t(
          "Hides every lesson that shipped with the platform. Lessons teachers created stay. Nothing is deleted, and you can restore them."
        )}
      </Text>

      {loading && !data ? <ActivityIndicator size="small" color={colors.accentPrimary} /> : null}
      {loadError ? <Text style={styles.error}>{loadError}</Text> : null}
      {!loading && !loadError && live.length === 0 ? (
        <Text style={styles.muted}>{t("No visible seeded lessons to remove.")}</Text>
      ) : null}

      <View style={styles.row}>
        <TouchableOpacity
          onPress={() => setPreviewOpen(true)}
          disabled={!canRemove}
          accessibilityRole="button"
          style={[styles.dangerBtn, !canRemove && styles.disabled]}
        >
          <Text style={styles.btnText}>{t("Remove seeded lessons")}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => run("restore", "Restored {count} seeded lessons.")}
          disabled={!canRestore}
          accessibilityRole="button"
          style={[styles.secondaryBtn, !canRestore && styles.disabled]}
        >
          <Text style={styles.secondaryText}>{t("Restore seeded lessons")}</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.muted}>
        {t("Restores every hidden seeded lesson, including any you removed individually.")}
      </Text>

      {previewOpen ? (
        <View style={styles.preview}>
          <Text style={styles.previewTitle}>
            {t("{count} seeded lessons will be hidden", { count: live.length })}
          </Text>
          {live.map((l) => (
            <Text key={l.id} style={styles.item} numberOfLines={1}>
              {l.title}
            </Text>
          ))}
          {journeys.length > 0 ? (
            <View style={styles.warning}>
              <Text style={styles.warningText}>
                {t("These journeys use them and will show them as unavailable:")}
              </Text>
              {journeys.map((j) => (
                <Text key={j.id} style={styles.warningItem}>
                  {j.title}
                </Text>
              ))}
            </View>
          ) : null}
          {forks > 0 ? (
            <Text style={styles.muted}>
              {t("{count} teacher copies keep working.", { count: forks })}
            </Text>
          ) : null}
          <View style={styles.row}>
            <TouchableOpacity
              onPress={() => run("hide", "Hid {count} seeded lessons.")}
              disabled={busy}
              accessibilityRole="button"
              style={[styles.dangerBtn, busy && styles.disabled]}
            >
              <Text style={styles.btnText}>{t("Hide {count} lessons", { count: live.length })}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setPreviewOpen(false)}
              disabled={busy}
              accessibilityRole="button"
              style={styles.secondaryBtn}
            >
              <Text style={styles.secondaryText}>{t("Cancel")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {actionError ? (
        <Text style={styles.error} selectable>
          {actionError}
        </Text>
      ) : null}
      {message ? <Text style={styles.success}>{message}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: { marginTop: 16, paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.border },
  help: { fontSize: 12, color: colors.textMuted, lineHeight: 16, marginBottom: 10 },
  row: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 8 },
  dangerBtn: { backgroundColor: "#b42318", paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8 },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: colors.borderStrong,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 8,
  },
  btnText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  secondaryText: { color: colors.textPrimary, fontWeight: "600", fontSize: 12 },
  disabled: { opacity: 0.4 },
  preview: {
    marginTop: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderRadius: 8,
    backgroundColor: colors.subtleBg,
  },
  previewTitle: { fontSize: 13, fontWeight: "700", color: colors.textPrimary, marginBottom: 6 },
  item: { fontSize: 12, color: colors.textSecondary, lineHeight: 18 },
  warning: { marginTop: 10, padding: 10, borderRadius: 6, backgroundColor: "#fef3c7" },
  warningText: { fontSize: 12, color: "#92400e", fontWeight: "600" },
  warningItem: { fontSize: 12, color: "#92400e", marginTop: 2 },
  muted: { fontSize: 12, color: colors.textMuted, marginTop: 6 },
  error: { fontSize: 12, color: "#b42318", marginTop: 8 },
  success: { fontSize: 12, color: "#067647", marginTop: 8, fontWeight: "600" },
});

export default SeededLessonsPanel;
